import { addDagen, maandagVan, verschilDagen, weekdagIndex } from './datum';
import { Kalender } from './kalender';
import { geplandeUren } from './werkpakket';
import { agileUren } from './agile';
import type { ISODate, Project, Taak, Takenpakket } from './types';

export interface DekkingRij {
  expertise: string;
  specialisten: number; // engineers met ≥ 75%
  totaal: number; // engineers met deze expertise
  vraag: number;
  aanbod: number;
  dekking: number | null; // 0..n (1 = 100%)
  oordeel: 'geen vraag' | 'ruim' | 'voldoende' | 'krap' | 'tekort';
  tekort: number;
  kwetsbaar: boolean;
}

/** SPEC §3.10 – expertise-dekking over `weken` weken vanaf vandaag. */
export function expertiseDekking(opts: {
  kal: Kalender;
  projecten: Project[];
  taken: Taak[];
  expertises: string[];
  vandaag: ISODate;
  weken: number;
  offertesMeetellen: boolean;
  /** agile items in een sprint tellen als ingepland (§9.2) */
  takenpakketten?: Takenpakket[];
}): DekkingRij[] {
  const { kal, projecten, taken, expertises, vandaag, weken, offertesMeetellen } = opts;
  const tps = opts.takenpakketten ?? [];
  const eind = addDagen(vandaag, weken * 7 - 1);
  const actief = projecten.filter((p) => p.status === 'GEPLAND' || p.status === 'IN_UITVOERING' || (offertesMeetellen && p.status === 'OFFERTE'));

  // vraag per expertise
  const vraag = new Map<string, number>(expertises.map((x) => [x, 0]));
  for (const p of actief) {
    const tot = p.livegang && p.livegang > vandaag ? p.livegang : addDagen(vandaag, 182);
    const totaalDagen = Math.max(1, verschilDagen(vandaag, tot) + 1);
    const inPeriode = Math.max(0, verschilDagen(vandaag, eind < tot ? eind : tot) + 1);
    const factor = Math.min(1, inPeriode / totaalDagen);
    for (const b of p.expertiseBudget) {
      const ingepland =
        geplandeUren(taken.filter((t) => t.projectId === p.id && (t.expertises ?? []).some((x) => x.naam === b.naam))) +
        agileUren(tps.filter((t) => t.projectId === p.id && t.expertise === b.naam)).ingepland;
      const rest = Math.max(0, b.uren - ingepland);
      if (vraag.has(b.naam)) vraag.set(b.naam, vraag.get(b.naam)! + rest * factor);
    }
  }

  // vrije uren per engineer in de periode
  const dagen: ISODate[] = [];
  for (let d = vandaag; d <= eind; d = addDagen(d, 1)) dagen.push(d);
  const aanbod = new Map<string, number>(expertises.map((x) => [x, 0]));
  for (const e of kal.engineers) {
    if (e.inzet === 'NIET_CAPACITEIT' || e.actief === false) continue;
    const vrij = dagen.reduce((s, d) => s + Math.max(0, kal.vrij(e, d)), 0);
    const mijn = e.expertises.filter((x) => aanbod.has(x.naam));
    if (!mijn.length) continue;
    const totVraag = mijn.reduce((s, x) => s + (vraag.get(x.naam) ?? 0), 0);
    for (const x of mijn) {
      const aandeel = totVraag > 0 ? (vraag.get(x.naam) ?? 0) / totVraag : 1 / mijn.length;
      aanbod.set(x.naam, aanbod.get(x.naam)! + vrij * aandeel * ((x.niveau || 100) / 100));
    }
  }

  return expertises.map((naam) => {
    const v = Math.round(vraag.get(naam)! * 10) / 10;
    const a = Math.round(aanbod.get(naam)! * 10) / 10;
    const metExp = kal.engineers.filter((e) => e.actief !== false && e.expertises.some((x) => x.naam === naam));
    const spec = metExp.filter((e) => (e.expertises.find((x) => x.naam === naam)?.niveau ?? 0) >= 75).length;
    const dk = v > 0 ? a / v : null;
    const oordeel: DekkingRij['oordeel'] = dk == null ? 'geen vraag' : dk >= 1.2 ? 'ruim' : dk >= 1 ? 'voldoende' : dk >= 0.8 ? 'krap' : 'tekort';
    return {
      expertise: naam,
      specialisten: spec,
      totaal: metExp.length,
      vraag: v,
      aanbod: a,
      dekking: dk,
      oordeel,
      tekort: dk != null && dk < 1 ? Math.round(v - a) : 0,
      kwetsbaar: v > 0 && spec <= 1,
    };
  });
}

export interface BezettingCel {
  week: ISODate;
  gepland: number;
  bezet: number;
  /** sprintwerk (SPEC §9.7) */
  sprint: number;
  beschikbaar: number;
  pct: number | null;
  status: 'voorbij' | 'afwezig' | 'groen' | 'oranje' | 'rood';
  /** week ligt helemaal vóór de huidige week: hele week geteld (terugkijken) */
  verleden?: boolean;
}

/** Bezetting per engineer per week (8 weken). */
/** `sprint`: sprintwerk per dag (SPEC §9.7, `sprintBelasting`), telt mee in het percentage. */
export function bezetting(kal: Kalender, engineerId: string, vanafWeek: ISODate, weken: number, vandaag: ISODate, sprint?: Map<ISODate, { uren: number }>): BezettingCel[] {
  const e = kal.engineer(engineerId);
  if (!e) return [];
  const uit: BezettingCel[] = [];
  for (let w = 0; w < weken; w++) {
    const ma = addDagen(maandagVan(vanafWeek), w * 7);
    // deze week: vanaf vandaag; eerdere weken: de hele week (terugkijken, SPEC §4.9)
    const verleden = ma < maandagVan(vandaag);
    const dagen = Array.from({ length: 7 }, (_, i) => addDagen(ma, i)).filter((d) => verleden || d >= vandaag);
    if (!dagen.length) {
      uit.push({ week: ma, gepland: 0, bezet: 0, sprint: 0, beschikbaar: 0, pct: null, status: 'voorbij' });
      continue;
    }
    const gepland = dagen.reduce((s, d) => s + kal.gepland(e, d), 0);
    const bezet = dagen.reduce((s, d) => s + kal.bezetUren(e, d), 0);
    const beschikbaar = dagen.reduce((s, d) => s + kal.capaciteit(e, d), 0);
    const sp = dagen.reduce((s, d) => s + (sprint?.get(d)?.uren ?? 0), 0);
    const pct = beschikbaar > 0 ? (gepland + bezet + sp) / beschikbaar : null;
    uit.push({
      week: ma,
      gepland,
      bezet,
      sprint: Math.round(sp * 10) / 10,
      beschikbaar,
      pct,
      status: pct == null ? 'afwezig' : pct > 1 ? 'rood' : pct >= 0.8 ? 'oranje' : 'groen',
      ...(verleden ? { verleden: true } : {}),
    });
  }
  return uit;
}

export function isWeekdag(d: ISODate) {
  return weekdagIndex(d) <= 4;
}
